import React, { useState, useEffect } from "react";
import { api } from "../../services/api";

const CategoryProgressStats = () => {
  const [categoryStats, setCategoryStats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCategoryProgressStats = async () => {
      try {
        // First fetch all users
        const usersResponse = await api.users.getAll();
        if (!usersResponse.success) {
          throw new Error(usersResponse.message || "Failed to fetch users");
        }

        // Filter to only get students
        const students = usersResponse.data.filter(
          (user) => user.role === "USER" || user.role === "STUDENT"
        );

        if (students.length === 0) {
          setLoading(false);
          return;
        }

        // Get all categories
        const categoriesResponse = await api.categories.getAll();
        if (!categoriesResponse.success) {
          throw new Error(
            categoriesResponse.message || "Failed to fetch categories"
          );
        }

        const categories = categoriesResponse.data;

        // Initialize category stats with category info
        const initialCategoryStats = categories.map((category) => ({
          id: category.id,
          name: category.name,
          totalStudents: 0,
          averageProgress: 0,
          averageScore: 0,
          color: category.color || getRandomColor(),
          progressData: [],
        }));

        // For each student, get their progress data
        for (const student of students) {
          try {
            const progressResponse = await api.userProgress.getByUserId(
              student.id
            );

            if (
              progressResponse.success &&
              progressResponse.data &&
              progressResponse.data.categoryProgress
            ) {
              // For each category progress, update our stats
              progressResponse.data.categoryProgress.forEach((catProgress) => {
                const categoryIndex = initialCategoryStats.findIndex(
                  (cat) => cat.id === catProgress.categoryId
                );

                if (categoryIndex !== -1) {
                  initialCategoryStats[categoryIndex].totalStudents++;
                  initialCategoryStats[categoryIndex].progressData.push({
                    progress: catProgress.progress || 0,
                    score: catProgress.score || 0,
                  });
                }
              });
            }
          } catch (error) {
            console.error(
              `Error fetching progress for user ${student.id}:`,
              error
            );
          }
        }

        // Calculate averages and sort by most active categories
        const processedCategoryStats = initialCategoryStats
          .map((cat) => {
            // Calculate average progress and score if we have data
            if (cat.progressData.length > 0) {
              cat.averageProgress = Math.round(
                cat.progressData.reduce((sum, data) => sum + data.progress, 0) /
                  cat.progressData.length
              );

              cat.averageScore = Math.round(
                cat.progressData.reduce((sum, data) => sum + data.score, 0) /
                  cat.progressData.length
              );
            }

            // Remove the raw data as we don't need it anymore
            const { progressData, ...result } = cat;
            return result;
          })
          .filter((cat) => cat.totalStudents > 0)
          .sort((a, b) => b.totalStudents - a.totalStudents);

        // Take top 5 most active categories
        setCategoryStats(processedCategoryStats.slice(0, 5));
      } catch (error) {
        console.error("Error fetching category progress statistics:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategoryProgressStats();
  }, []);

  // Helper function to generate random colors for categories that don't have one
  const getRandomColor = () => {
    const colors = [
      "#4CAF50", // Green
      "#2196F3", // Blue
      "#FFC107", // Amber
      "#9C27B0", // Purple
      "#F44336", // Red
      "#009688", // Teal
      "#FF5722", // Deep Orange
      "#673AB7", // Deep Purple
      "#3F51B5", // Indigo
      "#E91E63", // Pink
    ];

    return colors[Math.floor(Math.random() * colors.length)];
  };

  return (
    <div className="col-xxl-4 col-md-6">
      <div className="card">
        <div className="card-header">
          <div className="d-flex align-items-center flex-wrap gap-2 justify-content-between">
            <h6 className="mb-2 fw-bold text-lg mb-0">Category Progress</h6>
          </div>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center py-4">
              Loading category statistics...
            </div>
          ) : categoryStats.length === 0 ? (
            <div className="text-center py-4">No category data available</div>
          ) : (
            <div className="d-flex flex-column gap-4">
              {categoryStats.map((category) => (
                <div key={category.id} className="d-flex flex-column gap-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="text-md fw-medium mb-0">{category.name}</h6>
                    <span
                      className="badge"
                      style={{ backgroundColor: category.color, color: "#fff" }}
                    >
                      {category.totalStudents}{" "}
                      {category.totalStudents === 1 ? "student" : "students"}
                    </span>
                  </div>

                  <div className="progress h-8-px bg-light-300">
                    <div
                      className="progress-bar"
                      role="progressbar"
                      style={{
                        width: `${category.averageProgress}%`,
                        backgroundColor: category.color,
                      }}
                      aria-valuenow={category.averageProgress}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    />
                  </div>

                  <div className="d-flex justify-content-between">
                    <small className="text-secondary-light">
                      Avg. Progress: {category.averageProgress}%
                    </small>
                    <small className="text-secondary-light">
                      Avg. Score: {category.averageScore}%
                    </small>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryProgressStats;
